import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

import { useUiFeedback } from "../../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createSuccessFeedback,
} from "../../../../context/ui-feedback/action";
import { TemplateField } from "./template-field";
import {
  useCreateTemplate,
  useUpdateTemplate,
} from "../../../../api/templates-api";

export const TemplateEditor = ({ currentTemplate, onSuccess }) => {
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const templateFormRef = useRef(null);
  const dragPerson = useRef(0);
  const draggedOverPerson = useRef(0);
  const [formValidated, setFormValidated] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (currentTemplate) {
      setTemplate({
        id: currentTemplate.id,
        deleted: currentTemplate.deleted,
        draft: currentTemplate.draft,
        name: currentTemplate.name,
        machineService: currentTemplate.machineService,
        fields: currentTemplate.fields.map((field) => {
          return {
            uuid: uuidv4(),
            required: field.required,
            name: field.name,
            worksheetTitle: field.worksheetTitle,
            description: field.description,
            photos: field.photos,
            text: field.text,
            options: field.options.map((option) => {
              return {
                uuid: uuidv4(),
                value: option,
              };
            }),
          };
        }),
      });
    } else {
      setTemplate({
        id: null,
        deleted: false,
        draft: true,
        name: "",
        fields: [generateEmptyField()],
      });
    }
  }, [currentTemplate]);

  const generateEmptyField = () => {
    return {
      uuid: uuidv4(),
      required: true,
      name: "",
      worksheetTitle: "",
      description: "",
      photos: false,
      text: false,
      options: [],
    };
  };

  const cloneTemplateToSave = (template) => {
    return {
      id: template.id,
      deleted: template.deleted,
      draft: template.draft,
      name: template.name,
      machineService: template.machineService,
      fields: template.fields.map((field) => {
        return {
          required: field.required,
          name: field.name,
          worksheetTitle: field.worksheetTitle,
          description: field.description,
          photos: field.photos,
          text: field.text,
          options: field.options.map((option) => {
            return option.value;
          }),
        };
      }),
    };
  };

  const onSubmitTemplate = async (event, draft) => {
    event.preventDefault();
    setFormValidated(true);
    if (templateFormRef.current.checkValidity()) {
      try {
        createWaiting(uiFeedbackDispatch);
        template.draft = draft;
        const clonedTemplateToSave = cloneTemplateToSave(template);

        if (clonedTemplateToSave?.id) {
          await updateTemplate(clonedTemplateToSave, (data) => {
            if (data instanceof Error) {
              throw data;
            } else {
              onSuccess(data);
              createSuccessFeedback(
                uiFeedbackDispatch,
                intl.formatMessage({
                  id: "page.edittemplate.success.update",
                }),
              );
            }
          });
        } else {
          await createTemplate(clonedTemplateToSave, (data) => {
            if (data instanceof Error) {
              throw data;
            } else {
              onSuccess(data);
              createSuccessFeedback(
                uiFeedbackDispatch,
                intl.formatMessage({
                  id: "page.newtemplate.success.createtemplate",
                }),
              );
            }
          });
        }
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.edittemplate.exception.create.title",
            }),
            intl.formatMessage({
              id: "page.edittemplate.exception.create.description",
            }),
          );
        }
      } finally {
        closeWaiting(uiFeedbackDispatch);
      }
    }
  };

  const addField = () => {
    const newFields = template.fields;
    newFields.push(generateEmptyField());
    setTemplate({
      ...template,
      fields: newFields,
    });
  };

  const updateFieldValue = (uuid, key, value) => {
    setTemplate({
      ...template,
      fields: template.fields.map((field) =>
        field.uuid === uuid ? { ...field, [key]: value } : field,
      ),
    });
  };

  const addFieldAfterField = (uuid) => {
    const fields = [...template.fields];
    const index = fields.findIndex((field) => field.uuid === uuid);
    fields.splice(index + 1, 0, generateEmptyField());
    setTemplate({
      ...template,
      fields: [...fields],
    });
  };

  const deleteField = (uuid) => {
    const fields = [...template.fields];
    const index = fields.findIndex((field) => field.uuid === uuid);
    fields.splice(index, 1);
    setTemplate({
      ...template,
      fields: [...fields],
    });
  };

  const moveUpField = (uuid) => {
    const index = template.fields.findIndex((field) => field.uuid === uuid);
    if (index === 0) {
      moveFieldFromTo(index, template.fields.length - 1);
    } else {
      moveFieldFromTo(index - 1, index);
    }
  };

  const moveDownField = (uuid) => {
    const index = template.fields.findIndex((field) => field.uuid === uuid);
    if (index === template.fields.length - 1) {
      moveFieldFromTo(index, 0);
    } else {
      moveFieldFromTo(index, index + 1);
    }
  };

  const handleSort = () => {
    if (!template?.deleted) {
      const fields = [...template.fields];
      const temp = fields[dragPerson.current];
      fields[dragPerson.current] = fields[draggedOverPerson.current];
      fields[draggedOverPerson.current] = temp;
      setTemplate({
        ...template,
        fields: [...fields],
      });
    }
  };

  const moveFieldFromTo = (from, to) => {
    const fields = [...template.fields];
    fields.splice(to, 0, fields.splice(from, 1)[0]);
    setTemplate({
      ...template,
      fields: [...fields],
    });
  };

  const VersionInfo = ({ template }) => {
    if (!template?.id) {
      return null;
    }
    return (
      <div className="mb-4">
        <table>
          <tbody>
            <tr>
              <td>
                <span className="me-2">
                  <FormattedMessage id="data.template.data.createdUser" />:
                </span>
              </td>
              <td>
                {template.createdUser?.name} ({template.createdUser?.email})
              </td>
            </tr>
            <tr>
              <td>
                <span className="me-2">
                  <FormattedMessage id="data.template.data.createdDate" />:
                </span>
              </td>
              <td>{template.createdDate}</td>
            </tr>
            <tr>
              <td>
                <span className="me-2">
                  <FormattedMessage id="data.template.data.version" />:
                </span>
              </td>
              <td>
                {template.draft
                  ? `${intl.formatMessage({ id: "data.template.data.status.value.draft" })}`
                  : `${template.version}`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  if (!template) {
    return null;
  }

  return (
    <Form
      ref={templateFormRef}
      validated={formValidated}
      onSubmit={onSubmitTemplate}
    >
      <Form.Group className="mb-3">
        <Form.Label>
          <FormattedMessage id="data.template.form.name.label" />
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={intl.formatMessage({
            id: "data.template.form.name.placeholder",
          })}
          value={template?.name}
          required
          readOnly={template?.deleted}
          onChange={(event) => {
            setTemplate({ ...template, name: event.target.value });
          }}
        />
      </Form.Group>
      <VersionInfo template={currentTemplate} />
      <div className="mb-3">
        <Form.Check
          type="checkbox"
          className="mb-2"
          id={`template-machine-service`}
          label={intl.formatMessage({
            id: "data.template.form.machineservice",
          })}
          defaultChecked={template.machineService}
          disabled={template?.deleted}
          onChange={(event) => {
            setTemplate({ ...template, machineService: event.target.checked });
          }}
        />
      </div>
      <div>
        {template.fields.map((field, index) => (
          <div
            key={field.uuid}
            draggable
            onDragStart={() => {
              dragPerson.current = index;
            }}
            onDragEnter={() => {
              draggedOverPerson.current = index;
            }}
            onDragEnd={handleSort}
            onDragOver={(e) => e.preventDefault()}
          >
            <TemplateField
              rowNumber={index}
              index={field.uuid}
              field={field}
              readOnly={template?.deleted}
              handleUpdateFieldValue={(key, value) =>
                updateFieldValue(field.uuid, key, value)
              }
              handleAddField={() => addFieldAfterField(field.uuid)}
              handleDelete={() => deleteField(field.uuid)}
              handleMoveUp={() => moveUpField(field.uuid)}
              handleMoveDown={() => moveDownField(field.uuid)}
            />
          </div>
        ))}
      </div>
      {!template?.deleted ? (
        <>
          <div className="text-end">
            <Button variant="outline-primary" onClick={addField}>
              <FormattedMessage id="page.edittemplate.button.addField" />
            </Button>
          </div>
          <div>
            <Button
              className="me-2"
              variant="primary"
              onClick={(event) => onSubmitTemplate(event, false)}
            >
              <FormattedMessage id="page.edittemplate.button.save" />
            </Button>
            {template.draft ? (
              <Button
                variant="outline-primary"
                onClick={(event) => onSubmitTemplate(event, true)}
              >
                <FormattedMessage id="page.edittemplate.button.saveasdraft" />
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </Form>
  );
};

TemplateEditor.propTypes = {
  currentTemplate: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};
